import { call, put, takeLatest } from 'redux-saga/effects';
import { apiFunctions } from '../../api/functions';
import { errorHandler } from '../../utils/errorHandling';
import {
  // Main assessment
  getFunctionalSafetyAssessmentSuccess,
  getFunctionalSafetyAssessmentFailure,
  upsertFunctionalSafetyAssessmentSuccess,
  upsertFunctionalSafetyAssessmentFailure,

  // Import
  importFunctionalSafetySuccess,
  importFunctionalSafetyFailure,

  // MTTF Assessment
  createMTTFAssessmentSuccess,
  createMTTFAssessmentFailure,
  updateMTTFAssessmentSuccess,
  updateMTTFAssessmentFailure,
  deleteMTTFAssessmentSuccess,
  deleteMTTFAssessmentFailure,

  // DCavg Assessment
  createDCavgAssessmentSuccess,
  createDCavgAssessmentFailure,
  updateDCavgAssessmentSuccess,
  updateDCavgAssessmentFailure,
  deleteDCavgAssessmentSuccess,
  deleteDCavgAssessmentFailure,

  // CCF Assessment
  createCCFAssessmentSuccess,
  createCCFAssessmentFailure,
  deleteCCFAssessmentSuccess,
  deleteCCFAssessmentFailure,

  // HSR Assessment
  createHSRAssessmentSuccess,
  createHSRAssessmentFailure,
  deleteHSRAssessmentSuccess,
  deleteHSRAssessmentFailure,

  // Options
  getCCFOptionsSuccess,
  getCCFOptionsFailure,
  getHSROptionsSuccess,
  getHSROptionsFailure,

  // Stats
  getFunctionalSafetyStatsSuccess,
  getFunctionalSafetyStatsFailure,
} from './functionalSafetySlice';

// Main Assessment Sagas
function* getFunctionalSafetyAssessmentSaga({ payload }) {
  try {
   
    const response = yield call(apiFunctions.getFunctionalSafetyAssessment, payload);
    

    const assessment = Array.isArray(response.data) ? response.data[0] : response.data;
    

    yield put(getFunctionalSafetyAssessmentSuccess(assessment || null));
  } catch (error) {
    console.log('Saga: API Error:', error);
    const errorMessage = errorHandler(error);
    yield put(getFunctionalSafetyAssessmentFailure(errorMessage));
  }
}

function* upsertFunctionalSafetyAssessmentSaga({ payload }) {
  try {
    const response = yield call(apiFunctions.upsertFunctionalSafetyAssessment, payload);
    yield put(upsertFunctionalSafetyAssessmentSuccess(response.data));

    // Refetch full assessment so CCF not-applicable and nested data stay in sync (e.g. after reload)
    if (payload.task) {
      yield put({ type: 'functionalSafety/getFunctionalSafetyAssessment', payload: { task: payload.task } });
      yield* fetchStatsAfterUpdate(payload.task);
    }
  } catch (error) {
    console.log('Saga: Upsert failed, error:', error);
    const errorMessage = errorHandler(error);
    yield put(upsertFunctionalSafetyAssessmentFailure(errorMessage));
  }
}

// MTTF Assessment Sagas
function* createMTTFAssessmentSaga({ payload }) {
  try {
    const { assessmentId, assessmentData, taskId } = payload;
    const response = yield call(apiFunctions.createMTTFAssessment, assessmentId, assessmentData);
    yield put(createMTTFAssessmentSuccess(response.data));
    
    // Fetch stats after create
    if (taskId) {
      yield* fetchStatsAfterUpdate(taskId);
    }
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(createMTTFAssessmentFailure(errorMessage));
  }
}

function* updateMTTFAssessmentSaga({ payload }) {
  try {
    const { assessmentId, mttfId, assessmentData, taskId } = payload;
    const response = yield call(apiFunctions.updateMTTFAssessment, assessmentId, mttfId, assessmentData);
    yield put(updateMTTFAssessmentSuccess(response.data));
    
    // Fetch stats after update
    if (taskId) {
      yield* fetchStatsAfterUpdate(taskId);
    }
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(updateMTTFAssessmentFailure(errorMessage));
  }
}

function* deleteMTTFAssessmentSaga({ payload }) {
  try {
    const { assessmentId, mttfId, taskId } = payload;
    const response = yield call(apiFunctions.deleteMTTFAssessment, assessmentId, mttfId);
    yield put(deleteMTTFAssessmentSuccess(response.data));
    
    // Fetch stats after delete
    if (taskId) {
      yield* fetchStatsAfterUpdate(taskId);
    }
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(deleteMTTFAssessmentFailure(errorMessage));
  }
}

// DCavg Assessment Sagas
function* createDCavgAssessmentSaga({ payload }) {
  try {
    const { assessmentId, assessmentData, taskId } = payload;
    const response = yield call(apiFunctions.createDCavgAssessment, assessmentId, assessmentData);
    yield put(createDCavgAssessmentSuccess(response.data));
    
    // Fetch stats after create
    if (taskId) {
      yield* fetchStatsAfterUpdate(taskId);
    }
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(createDCavgAssessmentFailure(errorMessage));
  }
}

function* updateDCavgAssessmentSaga({ payload }) {
  try {
    const { assessmentId, dcavgId, assessmentData, taskId } = payload;
    const response = yield call(apiFunctions.updateDCavgAssessment, assessmentId, dcavgId, assessmentData);
    yield put(updateDCavgAssessmentSuccess(response.data));
    
    // Fetch stats after update
    if (taskId) {
      yield* fetchStatsAfterUpdate(taskId);
    }
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(updateDCavgAssessmentFailure(errorMessage));
  }
}

function* deleteDCavgAssessmentSaga({ payload }) {
  try {
    const { assessmentId, dcavgId, taskId } = payload;
    const response = yield call(apiFunctions.deleteDCavgAssessment, assessmentId, dcavgId);
    yield put(deleteDCavgAssessmentSuccess(response.data));
    
    // Fetch stats after delete
    if (taskId) {
      yield* fetchStatsAfterUpdate(taskId);
    }
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(deleteDCavgAssessmentFailure(errorMessage));
  }
}

// CCF Assessment Sagas
function* createCCFAssessmentSaga({ payload }) {
  try {
    const { assessmentId, assessmentData, taskId } = payload;
    const response = yield call(apiFunctions.createCCFAssessment, assessmentId, assessmentData);
    yield put(createCCFAssessmentSuccess(response.data));
    
    // Fetch stats after create
    if (taskId) {
      yield* fetchStatsAfterUpdate(taskId);
    }
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(createCCFAssessmentFailure(errorMessage));
  }
}

function* deleteCCFAssessmentSaga({ payload }) {
  try {
    const { assessmentId, ccfId, taskId } = payload;
    const response = yield call(apiFunctions.deleteCCFAssessment, assessmentId, ccfId);
    yield put(deleteCCFAssessmentSuccess(response.data));
    
    // Fetch stats after delete
    if (taskId) {
      yield* fetchStatsAfterUpdate(taskId);
    }
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(deleteCCFAssessmentFailure(errorMessage));
  }
}

// HSR Assessment Sagas
function* createHSRAssessmentSaga({ payload }) {
  try {
    const { assessmentId, assessmentData, taskId } = payload;
    const response = yield call(apiFunctions.createHSRAssessment, assessmentId, assessmentData);
    yield put(createHSRAssessmentSuccess(response.data));
    
    // Fetch stats after create
    if (taskId) {
      yield* fetchStatsAfterUpdate(taskId);
    }
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(createHSRAssessmentFailure(errorMessage));
  }
}

function* deleteHSRAssessmentSaga({ payload }) {
  try {
    const { assessmentId, hsrId, taskId } = payload;
    const response = yield call(apiFunctions.deleteHSRAssessment, assessmentId, hsrId);
    yield put(deleteHSRAssessmentSuccess(response.data));
    
    // Fetch stats after delete
    if (taskId) {
      yield* fetchStatsAfterUpdate(taskId);
    }
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(deleteHSRAssessmentFailure(errorMessage));
  }
}

// CCF Options Saga
function* getCCFOptionsSaga() {
  try {
    const response = yield call(apiFunctions.getCCFOptions);
    yield put(getCCFOptionsSuccess(response.data));
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(getCCFOptionsFailure(errorMessage));
  }
}

// HSR Options Saga
function* getHSROptionsSaga() {
  try {
    const response = yield call(apiFunctions.getHSROptions);
    yield put(getHSROptionsSuccess(response.data));
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(getHSROptionsFailure(errorMessage));
  }
}

// Stats Saga
function* getFunctionalSafetyStatsSaga({ payload }) {
  try {
    const response = yield call(apiFunctions.getFunctionalSafetyStats, payload);
    yield put(getFunctionalSafetyStatsSuccess(response.data));
  } catch (error) {
    const errorMessage = errorHandler(error);
    yield put(getFunctionalSafetyStatsFailure(errorMessage));
  }
}

// Helper function to fetch stats after assessment updates
function* fetchStatsAfterUpdate(taskId) {
  if (taskId) {
    yield put({ type: 'functionalSafety/getFunctionalSafetyStats', payload: { task: taskId } });
  }
}

// Import Functional Safety Saga
async function importFunctionalSafetyHandlerAPI(file, productId, machineId) {
  return await apiFunctions.importFunctionalSafety(file, productId, machineId);
}

function* importFunctionalSafetyHandler({payload}) {
  try {
    const result = yield call(
      importFunctionalSafetyHandlerAPI,
      payload.file,
      payload.productId,
      payload.machineId
    );
    payload.callback && payload.callback(result.data);
    yield put(importFunctionalSafetySuccess(result.data));
  } catch (error) {
    const err = errorHandler(error);
    // Call callback with error response so component can reset loading state
    payload.callback && payload.callback({success: false, error: err.error || err.message || 'Import failed'});
    yield put(importFunctionalSafetyFailure(err));
  }
}

// Root Saga
export default function* functionalSafetySaga() {
  yield takeLatest('functionalSafety/getFunctionalSafetyAssessment', getFunctionalSafetyAssessmentSaga);
  yield takeLatest('functionalSafety/upsertFunctionalSafetyAssessment', upsertFunctionalSafetyAssessmentSaga);

  yield takeLatest('functionalSafety/createMTTFAssessment', createMTTFAssessmentSaga);
  yield takeLatest('functionalSafety/updateMTTFAssessment', updateMTTFAssessmentSaga);
  yield takeLatest('functionalSafety/deleteMTTFAssessment', deleteMTTFAssessmentSaga);

  yield takeLatest('functionalSafety/createDCavgAssessment', createDCavgAssessmentSaga);
  yield takeLatest('functionalSafety/updateDCavgAssessment', updateDCavgAssessmentSaga);
  yield takeLatest('functionalSafety/deleteDCavgAssessment', deleteDCavgAssessmentSaga);

  yield takeLatest('functionalSafety/createCCFAssessment', createCCFAssessmentSaga);
  yield takeLatest('functionalSafety/deleteCCFAssessment', deleteCCFAssessmentSaga);

  yield takeLatest('functionalSafety/createHSRAssessment', createHSRAssessmentSaga);
  yield takeLatest('functionalSafety/deleteHSRAssessment', deleteHSRAssessmentSaga);

  yield takeLatest('functionalSafety/getCCFOptions', getCCFOptionsSaga);
  yield takeLatest('functionalSafety/getHSROptions', getHSROptionsSaga);
  
  yield takeLatest('functionalSafety/getFunctionalSafetyStats', getFunctionalSafetyStatsSaga);

  yield takeLatest('functionalSafety/importFunctionalSafety', importFunctionalSafetyHandler);
}
